/**
 * ServersV2.jsx - Server Management Panel (Proxmox-Style Layout)
 *
 * Features:
 * - Left sidebar with server list (name + IP)
 * - Main content with tabs: Server Details | Virtual Machines
 * - First server auto-selected on load
 * - All existing functionality preserved
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    Server, HardDrive, Cpu, MemoryStick, Database, RefreshCw, Play, Square, RotateCcw,
    Copy, Trash2, Settings, ChevronDown, ChevronRight, AlertTriangle, CheckCircle,
    XCircle, Clock, Search, Filter, ExternalLink, Wifi, WifiOff, X, Plus, Download, Upload,
    Monitor, Activity, Info, ListTodo, StopCircle
} from 'lucide-react'
import { monitoringApi, templateApi, dialerApi } from '../services/api'
import './ServersV2.css'

// Capacity thresholds
const VM_CAPACITY = {
    available: 39,
    full: 49,
    overloaded: 50,
    critical: 50
}

const RESOURCE_THRESHOLDS = {
    cpu: { warning: 70, critical: 90 },
    ram: { warning: 80, critical: 95 },
    disk: { warning: 80, critical: 95 }
}

// Searchable Dropdown Component
function SearchableDropdown({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    allLabel = 'All',
    showAll = true,
    displayKey = 'name',
    valueKey = 'id'
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')
    const dropdownRef = useRef(null)
    const inputRef = useRef(null)

    const selectedOption = options.find(opt => String(opt[valueKey]) === String(value))

    const filteredOptions = useMemo(() => {
        if (!search) return options
        const q = search.toLowerCase()
        return options.filter(opt => opt[displayKey]?.toLowerCase().includes(q))
    }, [options, search, displayKey])

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false)
                setSearch('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus()
        }
    }, [isOpen])

    const handleSelect = (optValue) => {
        onChange(optValue)
        setIsOpen(false)
        setSearch('')
    }

    return (
        <div className="searchable-dropdown" ref={dropdownRef}>
            <button
                type="button"
                className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="dropdown-value">
                    {selectedOption ? selectedOption[displayKey] : (showAll ? allLabel : placeholder)}
                </span>
                <ChevronDown size={14} className={`dropdown-chevron ${isOpen ? 'rotated' : ''}`} />
            </button>

            {isOpen && (
                <div className="dropdown-menu">
                    <div className="dropdown-search">
                        <Search size={14} />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={placeholder}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button className="clear-search" onClick={() => setSearch('')}>
                                <X size={12} />
                            </button>
                        )}
                    </div>
                    <div className="dropdown-options">
                        {showAll && (
                            <div
                                className={`dropdown-option ${!value ? 'selected' : ''}`}
                                onClick={() => handleSelect('')}
                            >
                                {allLabel}
                            </div>
                        )}
                        {filteredOptions.length === 0 ? (
                            <div className="dropdown-empty">No results found</div>
                        ) : (
                            filteredOptions.map(opt => (
                                <div
                                    key={opt[valueKey]}
                                    className={`dropdown-option ${String(opt[valueKey]) === String(value) ? 'selected' : ''}`}
                                    onClick={() => handleSelect(opt[valueKey])}
                                >
                                    {opt[displayKey]}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default function ServersV2() {
    const [searchParams, setSearchParams] = useSearchParams()

    // State
    const [servers, setServers] = useState([])
    const [allVMs, setAllVMs] = useState([])
    const [templates, setTemplates] = useState([])
    const [dialers, setDialers] = useState([])
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [refreshInterval, setRefreshInterval] = useState(60000)

    // Selected server and active tab
    const [selectedServerId, setSelectedServerId] = useState(null)
    const [activeTab, setActiveTab] = useState('vms') // 'info' or 'vms'

    // VM filters
    const [vmTab, setVmTab] = useState('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [dialerFilter, setDialerFilter] = useState('')
    const [serverSearch, setServerSearch] = useState('')

    // Modals
    const [cloneModal, setCloneModal] = useState(null)
    const [bulkCloneModal, setBulkCloneModal] = useState(null)
    const [configModal, setConfigModal] = useState(null)
    const [confirmDialog, setConfirmDialog] = useState(null)
    const [deleteVMDialog, setDeleteVMDialog] = useState(null)
    const [addServerWizard, setAddServerWizard] = useState(false)
    const [templateSelectModal, setTemplateSelectModal] = useState(null)

    // Toast
    const [toast, setToast] = useState(null)

    // Clone jobs
    const [cloneJobs, setCloneJobs] = useState([])
    const [bulkCloneQueues, setBulkCloneQueues] = useState([])
    const [serverSetupJobs, setServerSetupJobs] = useState([])

    // Background refresh
    const [backgroundRefreshing, setBackgroundRefreshing] = useState(false)

    // Multi-select for VMs
    const [selectedVMs, setSelectedVMs] = useState(new Set())
    const [bulkActionLoading, setBulkActionLoading] = useState(false)

    // VM action loading states
    const [vmActionLoading, setVmActionLoading] = useState({})

    // Inline editing
    const [editingVmId, setEditingVmId] = useState(null)
    const [editingVmName, setEditingVmName] = useState('')

    const showToast = useCallback((message, type = 'info') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 4000)
    }, [])

    // Get selected server object
    const selectedServer = useMemo(() => {
        return servers.find(s => s.id === selectedServerId)
    }, [servers, selectedServerId])

    // Fetch data
    const fetchData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true)
        else setLoading(true)

        try {
            const [serversRes, templatesRes, dialersRes] = await Promise.allSettled([
                monitoringApi.getServersStats(),
                templateApi.getAll(),
                dialerApi.getAll()
            ])

            if (serversRes.status === 'fulfilled' && serversRes.value.data.success) {
                const serverList = serversRes.value.data.servers || []
                setServers(serverList)

                // Auto-select first server if none selected
                if (!selectedServerId && serverList.length > 0) {
                    setSelectedServerId(serverList[0].id)
                }
            }

            if (templatesRes.status === 'fulfilled') {
                setTemplates(templatesRes.value.data || [])
            }

            if (dialersRes.status === 'fulfilled') {
                setDialers(dialersRes.value.data || [])
            }
        } catch (err) {
            console.error('Failed to fetch data:', err)
            showToast('Failed to load server data', 'error')
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [selectedServerId, showToast])

    // Track if VMs have been loaded for current server
    const [vmsLoaded, setVmsLoaded] = useState(false)
    const [vmsLoading, setVmsLoading] = useState(false)

    // Fetch VMs only when VMs tab is clicked (not on server selection)
    const fetchVMsForServer = useCallback(async () => {
        if (!selectedServer || vmsLoading) return

        setVmsLoading(true)
        try {
            const res = await monitoringApi.getServerVMs(selectedServer.ip_address)
            if (res.data.success) {
                setAllVMs(res.data.vms || [])
                setVmsLoaded(true)
            }
        } catch (err) {
            console.error('Failed to load VMs:', err)
            setAllVMs([])
        } finally {
            setVmsLoading(false)
        }
    }, [selectedServer, vmsLoading])

    // Reset VMs loaded state when server changes, and reload if on VMs tab
    const prevServerIdRef = useRef(selectedServerId)
    useEffect(() => {
        const serverChanged = prevServerIdRef.current !== selectedServerId
        prevServerIdRef.current = selectedServerId

        // Only clear VM data when the selected server actually changes
        if (serverChanged) {
            setVmsLoaded(false)
            setAllVMs([])
        }

        // If we're on VMs tab, fetch VMs (silently if just a refresh)
        if (activeTab === 'vms' && selectedServerId && servers.length > 0) {
            const server = servers.find(s => s.id === selectedServerId)
            if (server) {
                if (!vmsLoaded || serverChanged) setVmsLoading(true)
                monitoringApi.getServerVMs(server.ip_address)
                    .then(res => {
                        if (res.data.success) {
                            setAllVMs(res.data.vms || [])
                            setVmsLoaded(true)
                        }
                    })
                    .catch(err => {
                        console.error('Failed to load VMs:', err)
                        if (serverChanged) setAllVMs([])
                    })
                    .finally(() => setVmsLoading(false))
            }
        }
    }, [selectedServerId, activeTab, servers])

    // Handle tab change - fetch VMs if switching to VMs tab
    const handleTabChange = (tab) => {
        setActiveTab(tab)
        if (tab === 'vms' && !vmsLoaded && selectedServer) {
            fetchVMsForServer()
        }
    }

    const fetchDataRef = useRef(fetchData)
    useEffect(() => {
        fetchDataRef.current = fetchData
    }, [fetchData])

    // Initial load
    useEffect(() => {
        fetchData()
        setBackgroundRefreshing(true)
        monitoringApi.refreshAllServers().catch(err => {
            console.error('Background refresh failed:', err)
        }).finally(() => {
            setBackgroundRefreshing(false)
            fetchDataRef.current(true)
        })
    }, [])

    // Auto-refresh interval
    useEffect(() => {
        if (refreshInterval === 0) return
        const interval = setInterval(async () => {
            try {
                await monitoringApi.refreshAllServers()
            } catch (err) {
                console.error('Auto-refresh failed:', err)
            }
            fetchDataRef.current(true)
        }, refreshInterval)
        return () => clearInterval(interval)
    }, [refreshInterval])

    // Fast polling during setup
    useEffect(() => {
        const hasSetupInProgress = servers.some(s =>
            s.setup_status === 'downloading' || s.setup_status === 'restoring'
        )
        if (!hasSetupInProgress) return

        const fastPollInterval = setInterval(() => {
            fetchDataRef.current(true)
        }, 3000)

        return () => clearInterval(fastPollInterval)
    }, [servers])

    // URL params handling
    useEffect(() => {
        if (loading || servers.length === 0) return

        const ipParam = searchParams.get('ip')
        const cloneParam = searchParams.get('clone')
        const bulkCloneParam = searchParams.get('bulkClone')

        if (ipParam) {
            const server = servers.find(s => s.ip_address === ipParam)
            if (server) {
                setSelectedServerId(server.id)
                setActiveTab('vms')
            }
        }

        // Legacy clone param - redirect to bulk clone (template-only)
        if (cloneParam && ipParam && templates.length > 0) {
            const server = servers.find(s => s.ip_address === ipParam)
            if (server) {
                // Open bulk clone modal instead of individual VM clone
                setBulkCloneModal({ server })
                setSearchParams({})
            }
        }

        if (bulkCloneParam && ipParam && templates.length > 0) {
            const server = servers.find(s => s.ip_address === ipParam)
            if (server) {
                setBulkCloneModal({ server })
                setSearchParams({})
            }
        }
    }, [loading, servers, templates, searchParams, setSearchParams])

    // Poll clone jobs
    useEffect(() => {
        if (cloneJobs.length === 0) return

        const activeJobs = cloneJobs.filter(j => j.status !== 'completed' && j.status !== 'failed')
        if (activeJobs.length === 0) return

        const pollInterval = setInterval(async () => {
            const updates = await Promise.all(
                activeJobs.map(async (job) => {
                    try {
                        const res = await monitoringApi.getCloneProgress(job.jobId)
                        return res.data
                    } catch (e) {
                        return { jobId: job.jobId, status: 'failed', error: e.message }
                    }
                })
            )

            setCloneJobs(prev => {
                const updated = [...prev]
                updates.forEach(update => {
                    if (!update.found && !update.error) return
                    const idx = updated.findIndex(j => j.jobId === update.jobId)
                    if (idx !== -1) {
                        updated[idx] = { ...updated[idx], ...update }
                        if (update.status === 'completed') {
                            showToast(`Clone completed: ${update.vmName}`, 'success')
                            fetchData(true)
                        } else if (update.status === 'failed') {
                            showToast(`Clone failed: ${update.error || 'Unknown error'}`, 'error')
                        }
                    }
                })
                return updated
            })
        }, 2000)

        return () => clearInterval(pollInterval)
    }, [cloneJobs, showToast, fetchData])

    const dismissCloneJob = (jobId) => {
        setCloneJobs(prev => prev.filter(j => j.jobId !== jobId))
    }

    const dismissServerSetupJob = (jobId) => {
        setServerSetupJobs(prev => prev.filter(j => j.jobId !== jobId))
    }

    // Poll server setup jobs
    useEffect(() => {
        if (serverSetupJobs.length === 0) return

        const activeJobs = serverSetupJobs.filter(j => j.status !== 'completed' && j.status !== 'failed')
        if (activeJobs.length === 0) return

        const pollInterval = setInterval(async () => {
            for (const job of activeJobs) {
                try {
                    const result = await monitoringApi.getServerSetupProgress(job.jobId)
                    if (result.data.found) {
                        setServerSetupJobs(prev => prev.map(j =>
                            j.jobId === job.jobId ? { ...j, ...result.data } : j
                        ))
                        if (result.data.status === 'completed') {
                            fetchData(true)
                        }
                    }
                } catch (err) {
                    console.error('Error polling server setup job:', err)
                }
            }
        }, 3000)

        return () => clearInterval(pollInterval)
    }, [serverSetupJobs])

    // Poll bulk clone queues
    useEffect(() => {
        if (bulkCloneQueues.length === 0) return

        const activeQueues = bulkCloneQueues.filter(q => q.status === 'running')
        if (activeQueues.length === 0) return

        const pollInterval = setInterval(async () => {
            const updates = await Promise.all(
                activeQueues.map(async (queue) => {
                    try {
                        const res = await monitoringApi.getBulkCloneProgress(queue.queueId)
                        return res.data
                    } catch (e) {
                        return { queueId: queue.queueId, status: 'failed', error: e.message }
                    }
                })
            )

            setBulkCloneQueues(prev => {
                const updated = [...prev]
                updates.forEach(update => {
                    if (!update.found && !update.error) return
                    const idx = updated.findIndex(q => q.queueId === update.queueId)
                    if (idx !== -1) {
                        updated[idx] = { ...updated[idx], ...update }
                        if (update.status === 'completed') {
                            showToast(`Bulk clone completed: ${update.completed}/${update.total} VMs`, 'success')
                            fetchData(true)
                        } else if (update.status === 'failed' && update.completed === 0) {
                            showToast(`Bulk clone failed`, 'error')
                        }
                    }
                })
                return updated
            })
        }, 3000)

        return () => clearInterval(pollInterval)
    }, [bulkCloneQueues, showToast, fetchData])

    const dismissBulkCloneQueue = (queueId) => {
        setBulkCloneQueues(prev => prev.filter(q => q.queueId !== queueId))
    }

    const cancelBulkClone = async (queueId) => {
        try {
            const res = await monitoringApi.cancelBulkClone(queueId)
            if (res.data.success) {
                showToast(res.data.message, 'warning')
                setBulkCloneQueues(prev => prev.map(q =>
                    q.queueId === queueId ? { ...q, status: 'cancelled' } : q
                ))
            } else {
                showToast(res.data.error || 'Failed to cancel', 'error')
            }
        } catch (err) {
            showToast(`Cancel failed: ${err.message}`, 'error')
        }
    }

    // Background processes panel
    const [bgProcessesOpen, setBgProcessesOpen] = useState(false)
    const [bgProcesses, setBgProcesses] = useState([])
    const [bgLoading, setBgLoading] = useState(false)

    const loadBgProcesses = async () => {
        setBgLoading(true)
        try {
            const res = await monitoringApi.getBulkCloneQueues()
            setBgProcesses(res.data.queues || [])
        } catch (err) {
            console.error('Failed to load background processes:', err)
        } finally {
            setBgLoading(false)
        }
    }

    const toggleBgProcesses = () => {
        if (!bgProcessesOpen) loadBgProcesses()
        setBgProcessesOpen(!bgProcessesOpen)
    }

    // Multi-select helpers
    const toggleVMSelection = (vmid) => {
        setSelectedVMs(prev => {
            const next = new Set(prev)
            if (next.has(vmid)) next.delete(vmid)
            else next.add(vmid)
            return next
        })
    }

    const selectAllVMs = () => {
        const filtered = filteredVMs
        if (selectedVMs.size === filtered.length) {
            setSelectedVMs(new Set())
        } else {
            setSelectedVMs(new Set(filtered.map(vm => vm.vmid)))
        }
    }

    const clearSelection = () => setSelectedVMs(new Set())

    // Bulk VM action
    const handleBulkVMAction = async (action) => {
        if (selectedVMs.size === 0 || !selectedServer) return

        if (action === 'delete') {
            setDeleteVMDialog({
                type: 'bulk',
                serverIP: selectedServer.ip_address,
                vmids: Array.from(selectedVMs)
            })
            return
        }

        await executeBulkAction(action)
    }

    const executeBulkAction = async (action, deleteLicense = false) => {
        setBulkActionLoading(true)
        try {
            const result = await monitoringApi.bulkVMAction(selectedServer.ip_address, Array.from(selectedVMs), action, deleteLicense)
            if (result.data.success) {
                const msg = action === 'delete' && deleteLicense
                    ? `Deleted ${result.data.succeeded}/${result.data.total} VMs with licenses`
                    : `${action}: ${result.data.succeeded}/${result.data.total} succeeded`
                showToast(msg, 'success')
                setSelectedVMs(new Set())
                fetchData(true)
            } else {
                showToast(`Bulk ${action} failed`, 'error')
            }
        } catch (err) {
            showToast(`Bulk ${action} failed: ${err.message}`, 'error')
        } finally {
            setBulkActionLoading(false)
        }
    }

    const executeBulkDelete = async (vmids, deleteLicense) => {
        setDeleteVMDialog(null)
        setBulkActionLoading(true)
        try {
            const result = await monitoringApi.bulkVMAction(selectedServer.ip_address, vmids, 'delete', deleteLicense)
            if (result.data.success) {
                const msg = deleteLicense
                    ? `Deleted ${result.data.succeeded}/${result.data.total} VMs with licenses`
                    : `Deleted ${result.data.succeeded}/${result.data.total} VMs`
                showToast(msg, 'success')
                setSelectedVMs(new Set())
                fetchData(true)
            } else {
                showToast('Bulk delete failed', 'error')
            }
        } catch (err) {
            showToast(`Bulk delete failed: ${err.message}`, 'error')
        } finally {
            setBulkActionLoading(false)
        }
    }

    // Refresh all
    const handleRefreshAll = async () => {
        setRefreshing(true)
        try {
            await monitoringApi.refreshAllServers()
            await fetchData(true)
            showToast('Server stats refreshed', 'success')
        } catch (err) {
            showToast('Failed to refresh servers', 'error')
        }
    }

    // VM Actions
    const handleVMAction = async (action, serverIP, vmid, vmName) => {
        setVmActionLoading(prev => ({ ...prev, [vmid]: action }))

        try {
            let result
            switch (action) {
                case 'start':
                    result = await monitoringApi.vmStart(serverIP, vmid)
                    break
                case 'stop':
                    result = await monitoringApi.vmStop(serverIP, vmid)
                    break
                case 'restart':
                    result = await monitoringApi.vmRestart(serverIP, vmid)
                    break
                default:
                    setVmActionLoading(prev => {
                        const next = { ...prev }
                        delete next[vmid]
                        return next
                    })
                    return
            }
            if (result.data.success) {
                showToast(`VM ${vmName} ${action} command sent`, 'success')
                setTimeout(() => fetchData(true), 2000)
            } else {
                showToast(result.data.error || `Failed to ${action} VM`, 'error')
            }
        } catch (err) {
            showToast(`Failed to ${action} VM: ${err.message}`, 'error')
        } finally {
            setVmActionLoading(prev => {
                const next = { ...prev }
                delete next[vmid]
                return next
            })
        }
    }

    // Delete VM
    const handleDeleteVM = (serverIP, vmid, vmName) => {
        setDeleteVMDialog({
            type: 'single',
            serverIP,
            vmid,
            vmName
        })
    }

    const executeDeleteVM = async (serverIP, vmid, vmName, deleteLicense) => {
        setDeleteVMDialog(null)
        setVmActionLoading(prev => ({ ...prev, [vmid]: 'delete' }))

        try {
            const result = await monitoringApi.deleteVM(serverIP, vmid, deleteLicense)
            if (result.data.success) {
                showToast(`VM ${vmName} deleted${deleteLicense ? ' with license' : ''}`, 'success')
                fetchData(true)
            } else {
                showToast(result.data.error || 'Failed to delete VM', 'error')
            }
        } catch (err) {
            showToast(`Failed to delete VM: ${err.message}`, 'error')
        } finally {
            setVmActionLoading(prev => {
                const next = { ...prev }
                delete next[vmid]
                return next
            })
        }
    }

    // Inline edit
    const startInlineEdit = (vm) => {
        setEditingVmId(vm.vmid)
        setEditingVmName(vm.name || '')
    }

    const saveInlineEdit = async (vmid) => {
        if (!editingVmName.trim() || !selectedServer) {
            setEditingVmId(null)
            return
        }

        setVmActionLoading(prev => ({ ...prev, [vmid]: 'rename' }))

        try {
            const result = await monitoringApi.renameVM(selectedServer.ip_address, vmid, editingVmName.trim())
            if (result.data.success) {
                showToast('VM renamed', 'success')
                fetchData(true)
            } else {
                showToast(result.data.error || 'Rename failed', 'error')
            }
        } catch (err) {
            showToast(`Rename failed: ${err.message}`, 'error')
        } finally {
            setEditingVmId(null)
            setVmActionLoading(prev => {
                const next = { ...prev }
                delete next[vmid]
                return next
            })
        }
    }

    const handleInlineKeyDown = (e, vmid) => {
        if (e.key === 'Enter') {
            saveInlineEdit(vmid)
        } else if (e.key === 'Escape') {
            setEditingVmId(null)
        }
    }

    // Start server setup
    const handleStartServerSetup = async (server) => {
        try {
            const templatesRes = await monitoringApi.getVMTemplates()
            if (!templatesRes.data.success || !templatesRes.data.templates?.length) {
                showToast('No templates available', 'error')
                return
            }
            setTemplateSelectModal({
                server,
                templates: templatesRes.data.templates
            })
        } catch (err) {
            showToast(err.response?.data?.error || err.message, 'error')
        }
    }

    const executeServerSetup = async (server, template) => {
        try {
            const result = await monitoringApi.startServerSetup(server.id, template.id)
            if (result.data.success) {
                showToast(`Setup started for ${server.ip_address}`, 'success')
                setTemplateSelectModal(null)
                fetchData(true)
            } else {
                showToast(result.data.error || 'Failed to start setup', 'error')
            }
        } catch (err) {
            showToast(err.response?.data?.error || err.message, 'error')
        }
    }

    // Stats calculations
    const stats = useMemo(() => {
        const totalServers = servers.length
        const onlineServers = servers.filter(s => s.online).length
        let totalVMs = 0, runningVMs = 0, totalCpu = 0, serversCounted = 0
        let ramUsed = 0, ramTotal = 0, diskUsed = 0, diskTotal = 0

        servers.forEach(s => {
            totalVMs += s.vm_count || 0
            runningVMs += s.vm_running || 0
            if (s.cpu_usage !== undefined && s.cpu_usage !== null) {
                totalCpu += s.cpu_usage
                serversCounted++
            }
            ramUsed += s.ram_used_gb || 0
            ramTotal += s.ram_total_gb || 0
            diskUsed += s.disk_used_gb || 0
            diskTotal += s.disk_total_gb || 0
        })

        return {
            totalServers,
            onlineServers,
            totalVMs,
            runningVMs,
            avgCpuUsage: serversCounted > 0 ? Math.round(totalCpu / serversCounted) : 0,
            ramPercent: ramTotal > 0 ? Math.round((ramUsed / ramTotal) * 100) : 0,
            diskPercent: diskTotal > 0 ? Math.round((diskUsed / diskTotal) * 100) : 0
        }
    }, [servers])

    // Alerts
    const alerts = useMemo(() => {
        const alertList = []
        servers.forEach(server => {
            if (server.vm_count > VM_CAPACITY.critical) {
                alertList.push({
                    type: 'critical',
                    message: `Server (${server.name || server.ip_address}) over capacity: ${server.vm_count} VMs`
                })
            }
            if (server.cpu_usage >= RESOURCE_THRESHOLDS.cpu.critical) {
                alertList.push({
                    type: 'critical',
                    message: `${server.name || server.ip_address} CPU critical: ${server.cpu_usage}%`
                })
            }
        })
        return alertList
    }, [servers])

    // Filtered VMs
    const filteredVMs = useMemo(() => {
        let vms = [...allVMs]
        const sevenDaysAgo = new Date()
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

        switch (vmTab) {
            case 'offline':
                vms = vms.filter(vm => vm.status !== 'running')
                break
            case 'unlinked':
                vms = vms.filter(vm => !vm.linked_sip)
                break
            case 'inactive':
                vms = vms.filter(vm => {
                    if (!vm.linked_sip) return false
                    if (!vm.last_heartbeat) return true
                    return new Date(vm.last_heartbeat) < sevenDaysAgo
                })
                break
        }

        if (searchQuery) {
            const q = searchQuery.toLowerCase()
            vms = vms.filter(vm =>
                vm.name?.toLowerCase().includes(q) ||
                String(vm.vmid).includes(q) ||
                vm.linked_sip?.toLowerCase().includes(q) ||
                vm.dialer_name?.toLowerCase().includes(q)
            )
        }

        if (dialerFilter) {
            vms = vms.filter(vm => vm.dialer_id === parseInt(dialerFilter))
        }

        vms.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        return vms
    }, [allVMs, vmTab, searchQuery, dialerFilter])

    // Filtered servers for sidebar
    const filteredServers = useMemo(() => {
        if (!serverSearch) return servers
        const q = serverSearch.toLowerCase()
        return servers.filter(s =>
            s.name?.toLowerCase().includes(q) ||
            s.ip_address?.toLowerCase().includes(q)
        )
    }, [servers, serverSearch])

    // Helper functions
    const getResourceColor = (value, type) => {
        const thresholds = RESOURCE_THRESHOLDS[type]
        if (value >= thresholds.critical) return 'critical'
        if (value >= thresholds.warning) return 'warning'
        return 'normal'
    }

    // Get status light color: red (>90%), yellow (>60%), green (<60%)
    const getStatusLightColor = (value) => {
        if (value >= 90) return 'red'
        if (value >= 60) return 'yellow'
        return 'green'
    }

    const getCapacityStatus = (vmCount) => {
        if (vmCount > VM_CAPACITY.critical) return { status: 'critical', label: 'Critical' }
        if (vmCount >= VM_CAPACITY.overloaded) return { status: 'overloaded', label: 'Overloaded' }
        if (vmCount >= VM_CAPACITY.full) return { status: 'full', label: 'Full' }
        return { status: 'available', label: 'Available' }
    }

    // Get all issues for a server
    const getServerIssues = (server) => {
        const issues = []
        const diskPercent = server.disk_total_gb ? Math.round((server.disk_used_gb / server.disk_total_gb) * 100) : 0
        const ramPercent = server.ram_total_gb ? Math.round((server.ram_used_gb / server.ram_total_gb) * 100) : 0

        // VM capacity issues
        if (server.vm_count > VM_CAPACITY.critical) {
            issues.push({ type: 'critical', message: `Over capacity: ${server.vm_count} VMs (max ${VM_CAPACITY.critical})` })
        } else if (server.vm_count >= VM_CAPACITY.overloaded) {
            issues.push({ type: 'warning', message: `Overloaded: ${server.vm_count} VMs` })
        }

        // Disk space issues
        if (diskPercent >= 95) {
            issues.push({ type: 'critical', message: `Disk critical: ${diskPercent}% used` })
        } else if (diskPercent >= 80) {
            issues.push({ type: 'warning', message: `Disk high: ${diskPercent}% used` })
        }

        // RAM issues
        if (ramPercent >= 95) {
            issues.push({ type: 'critical', message: `RAM critical: ${ramPercent}% used` })
        } else if (ramPercent >= 85) {
            issues.push({ type: 'warning', message: `RAM high: ${ramPercent}% used` })
        }

        // CPU issues
        if (server.cpu_usage >= 90) {
            issues.push({ type: 'warning', message: `CPU high: ${server.cpu_usage}%` })
        }

        return issues
    }

    const timeAgo = (date) => {
        if (!date) return 'Never'
        const seconds = Math.abs(Math.floor((new Date() - new Date(date)) / 1000))
        if (seconds < 60) return `${seconds}s ago`
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
        return `${Math.floor(seconds / 86400)}d ago`
    }

    if (loading) {
        return (
            <div className="servers-page-v2">
                <div className="loading-state">
                    <RefreshCw className="spin" size={32} />
                    <p>Loading servers...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="servers-page-v2">
            {/* Toast */}
            {toast && (
                <div className={`toast toast-${toast.type}`}>
                    {toast.type === 'success' && <CheckCircle size={16} />}
                    {toast.type === 'error' && <XCircle size={16} />}
                    {toast.type === 'warning' && <AlertTriangle size={16} />}
                    <span>{toast.message}</span>
                </div>
            )}

            {/* Clone Progress */}
            {cloneJobs.length > 0 && (
                <div className="clone-progress-container">
                    {cloneJobs.map(job => (
                        <div key={job.jobId} className={`clone-progress-item status-${job.status}`}>
                            <div className="clone-progress-header">
                                <span className="clone-vm-name">{job.vmName}</span>
                                <span className="clone-status">{job.message || job.status}</span>
                                {(job.status === 'completed' || job.status === 'failed') && (
                                    <button className="dismiss-btn" onClick={() => dismissCloneJob(job.jobId)}>
                                        &times;
                                    </button>
                                )}
                            </div>
                            <div className="clone-progress-bar">
                                <div className="clone-progress-fill" style={{ width: `${job.progress || 0}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Bulk Clone Progress */}
            {bulkCloneQueues.length > 0 && (
                <div className="bulk-clone-progress-container">
                    {bulkCloneQueues.map(queue => (
                        <div key={queue.queueId} className={`bulk-clone-progress-item status-${queue.status}`}>
                            <div className="bulk-clone-header">
                                <span className="bulk-clone-title">
                                    Bulk Clone: {queue.completed}/{queue.total} completed
                                    {queue.failed > 0 && <span className="failed-count">({queue.failed} failed)</span>}
                                    {queue.status === 'cancelled' && <span className="failed-count">(cancelled)</span>}
                                </span>
                                {queue.status === 'running' && (
                                    <button className="cancel-bulk-btn" onClick={() => cancelBulkClone(queue.queueId)} title="Cancel remaining clones">
                                        <StopCircle size={14} /> Cancel
                                    </button>
                                )}
                                {(queue.status === 'completed' || queue.status === 'failed' || queue.status === 'cancelled') && (
                                    <button className="dismiss-btn" onClick={() => dismissBulkCloneQueue(queue.queueId)}>
                                        &times;
                                    </button>
                                )}
                            </div>
                            <div className="bulk-clone-progress-bar">
                                <div
                                    className="bulk-clone-progress-fill"
                                    style={{ width: `${queue.total > 0 ? (queue.completed / queue.total) * 100 : 0}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Background Processes Panel */}
            {bgProcessesOpen && (
                <div className="bg-processes-panel">
                    <div className="bg-processes-header">
                        <span><ListTodo size={14} /> Background Processes</span>
                        <button className="dismiss-btn" onClick={() => setBgProcessesOpen(false)}>&times;</button>
                    </div>
                    <div className="bg-processes-body">
                        {bgLoading ? (
                            <div className="bg-processes-empty"><RefreshCw className="spin" size={14} /> Loading...</div>
                        ) : bgProcesses.length === 0 ? (
                            <div className="bg-processes-empty">No background processes</div>
                        ) : (
                            bgProcesses.map(p => (
                                <div key={p.queueId} className={`bg-process-item status-${p.status}`}>
                                    <div className="bg-process-info">
                                        <span className="bg-process-name">Bulk Clone{p.templateName ? `: ${p.templateName}` : ''}</span>
                                        <span className="bg-process-status">
                                            {p.completed}/{p.total}
                                            {p.failed > 0 && ` (${p.failed} failed)`}
                                            {p.status === 'cancelled' && ' - Cancelled'}
                                            {p.status === 'completed' && ' - Done'}
                                            {p.status === 'failed' && ' - Failed'}
                                        </span>
                                    </div>
                                    <div className="bg-process-bar">
                                        <div className="bg-process-fill" style={{ width: `${p.total > 0 ? (p.completed / p.total) * 100 : 0}%` }} />
                                    </div>
                                    {p.status === 'running' && (
                                        <button className="cancel-bulk-btn" onClick={() => { cancelBulkClone(p.queueId); loadBgProcesses() }}>
                                            <StopCircle size={12} /> Cancel
                                        </button>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Main Layout: Sidebar + Content - full height */}
            <div className="servers-layout">
                {/* Server Sidebar */}
                <div className="server-sidebar">
                    <div className="sidebar-header">
                        <div className="sidebar-title">
                            <Server size={16} />
                            <span>Servers</span>
                            <span className="server-count-badge">{stats.onlineServers}/{stats.totalServers}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <button className="btn-icon" onClick={toggleBgProcesses} title="Background Processes">
                                <ListTodo size={14} />
                            </button>
                            <button className="btn-icon" onClick={() => setAddServerWizard(true)} title="Add Server">
                                <Plus size={14} />
                            </button>
                        </div>
                    </div>
                    <div className="sidebar-search">
                        <Search size={14} />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={serverSearch}
                            onChange={(e) => setServerSearch(e.target.value)}
                        />
                    </div>
                    <div className="server-list">
                        {filteredServers.map(server => {
                            const isSelected = server.id === selectedServerId
                            const ramPercent = server.ram_total_gb ? Math.round((server.ram_used_gb / server.ram_total_gb) * 100) : 0
                            const diskPercent = server.disk_total_gb ? Math.round((server.disk_used_gb / server.disk_total_gb) * 100) : 0
                            const issues = getServerIssues(server)
                            const hasCritical = issues.some(i => i.type === 'critical')
                            const hasWarning = issues.some(i => i.type === 'warning')

                            return (
                                <div
                                    key={server.id}
                                    className={`server-item ${isSelected ? 'selected' : ''} ${!server.online ? 'offline' : ''}`}
                                    onClick={() => setSelectedServerId(server.id)}
                                >
                                    <div className="server-item-indicator">
                                        {hasCritical ? (
                                            <AlertTriangle size={14} className="issue-icon critical" title={issues.filter(i => i.type === 'critical').map(i => i.message).join('\n')} />
                                        ) : hasWarning ? (
                                            <AlertTriangle size={14} className="issue-icon warning" title={issues.map(i => i.message).join('\n')} />
                                        ) : (
                                            <span className={`status-dot ${server.online ? 'online' : 'offline'}`} />
                                        )}
                                    </div>
                                    <div className="server-item-info">
                                        <div className="server-item-name">
                                            {server.name || server.ip_address}
                                        </div>
                                        <div className="server-item-meta">
                                            <span className="server-item-ip">{server.ip_address}</span>
                                            <div className="server-item-lights">
                                                <span
                                                    className={`status-light ${getStatusLightColor(server.cpu_usage || 0)}`}
                                                    title={`CPU: ${server.cpu_usage || 0}%`}
                                                />
                                                <span
                                                    className={`status-light ${getStatusLightColor(ramPercent)}`}
                                                    title={`RAM: ${ramPercent}%`}
                                                />
                                                <span
                                                    className={`status-light ${getStatusLightColor(diskPercent)}`}
                                                    title={`Disk: ${diskPercent}%`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="server-item-stats">
                                        <span className="vm-count-mini">
                                            {server.vm_running}/{server.vm_count}
                                        </span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* Main Content */}
                <div className="server-content">
                    {selectedServer ? (
                        <>
                            {/* Content Header with Tabs */}
                            <div className="content-header">
                                <div className="content-header-left">
                                    <h2>{selectedServer.name || selectedServer.ip_address}</h2>
                                    <span className={`status-badge-sm ${selectedServer.online ? 'online' : 'offline'}`}>
                                        {selectedServer.online ? 'Online' : 'Offline'}
                                    </span>
                                    <div className="content-tabs">
                                        <button
                                            className={`content-tab ${activeTab === 'info' ? 'active' : ''}`}
                                            onClick={() => handleTabChange('info')}
                                        >
                                            <Info size={12} />
                                            Info
                                        </button>
                                        <button
                                            className={`content-tab ${activeTab === 'vms' ? 'active' : ''}`}
                                            onClick={() => handleTabChange('vms')}
                                        >
                                            {vmsLoading ? (
                                                <RefreshCw size={12} className="spin" />
                                            ) : (
                                                <Monitor size={12} />
                                            )}
                                            VMs {vmsLoaded ? `(${allVMs.length})` : ''}
                                        </button>
                                    </div>
                                </div>
                                <div className="content-header-right">
                                    {backgroundRefreshing && (
                                        <RefreshCw size={12} className="spinning text-muted" />
                                    )}
                                    <select
                                        value={refreshInterval}
                                        onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                                        className="refresh-select-sm"
                                    >
                                        <option value={30000}>30s</option>
                                        <option value={60000}>60s</option>
                                        <option value={120000}>2m</option>
                                        <option value={0}>Off</option>
                                    </select>
                                    <button className="btn-icon" onClick={handleRefreshAll} disabled={refreshing} title="Refresh">
                                        <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                                    </button>
                                </div>
                            </div>

                            {/* Tab Content */}
                            {activeTab === 'info' && (
                                <ServerInfoTab
                                    server={selectedServer}
                                    onClone={() => setCloneModal({ server: selectedServer })}
                                    onBulkClone={() => setBulkCloneModal({ server: selectedServer })}
                                    onStartSetup={() => handleStartServerSetup(selectedServer)}
                                    getResourceColor={getResourceColor}
                                    getCapacityStatus={getCapacityStatus}
                                    timeAgo={timeAgo}
                                    issues={getServerIssues(selectedServer)}
                                />
                            )}

                            {activeTab === 'vms' && (
                                <VMsTab
                                    server={selectedServer}
                                    vms={filteredVMs}
                                    allVMs={allVMs}
                                    vmTab={vmTab}
                                    setVmTab={setVmTab}
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                    dialerFilter={dialerFilter}
                                    setDialerFilter={setDialerFilter}
                                    dialers={dialers}
                                    selectedVMs={selectedVMs}
                                    toggleVMSelection={toggleVMSelection}
                                    selectAllVMs={selectAllVMs}
                                    clearSelection={clearSelection}
                                    handleBulkVMAction={handleBulkVMAction}
                                    bulkActionLoading={bulkActionLoading}
                                    vmActionLoading={vmActionLoading}
                                    handleVMAction={handleVMAction}
                                    handleDeleteVM={handleDeleteVM}
                                    setCloneModal={setCloneModal}
                                    setBulkCloneModal={setBulkCloneModal}
                                    setConfigModal={setConfigModal}
                                    servers={servers}
                                    editingVmId={editingVmId}
                                    editingVmName={editingVmName}
                                    setEditingVmName={setEditingVmName}
                                    startInlineEdit={startInlineEdit}
                                    saveInlineEdit={saveInlineEdit}
                                    handleInlineKeyDown={handleInlineKeyDown}
                                    timeAgo={timeAgo}
                                />
                            )}
                        </>
                    ) : (
                        <div className="no-server-selected">
                            <Server size={48} />
                            <p>Select a server from the sidebar</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            {cloneModal && (
                <CloneVMModal
                    server={cloneModal.server}
                    templates={templates}
                    onClose={() => setCloneModal(null)}
                    onJobStarted={(job) => {
                        setCloneModal(null)
                        setCloneJobs(prev => [...prev, job])
                        showToast(`Clone started: ${job.vmName}`, 'info')
                    }}
                    onError={(msg) => showToast(msg, 'error')}
                />
            )}

            {configModal && (
                <ConfigVMModal
                    vm={configModal.vm}
                    serverIP={configModal.serverIP}
                    onClose={() => setConfigModal(null)}
                    onSuccess={() => {
                        setConfigModal(null)
                        fetchData(true)
                        showToast('VM configuration updated', 'success')
                    }}
                    onError={(msg) => showToast(msg, 'error')}
                />
            )}

            {bulkCloneModal && (
                <BulkCloneModal
                    server={bulkCloneModal.server}
                    templates={templates}
                    onClose={() => setBulkCloneModal(null)}
                    onQueueStarted={(queue) => {
                        setBulkCloneModal(null)
                        const formattedQueue = {
                            ...queue,
                            items: queue.jobs || queue.items || []
                        }
                        delete formattedQueue.jobs
                        setBulkCloneQueues(prev => [...prev, formattedQueue])
                        showToast(`Bulk clone started: ${queue.total} VMs`, 'info')
                    }}
                    onError={(msg) => showToast(msg, 'error')}
                />
            )}

            {deleteVMDialog && (
                <DeleteVMDialog
                    dialog={deleteVMDialog}
                    onClose={() => setDeleteVMDialog(null)}
                    onConfirmSingle={executeDeleteVM}
                    onConfirmBulk={executeBulkDelete}
                />
            )}

            {addServerWizard && (
                <AddServerWizard
                    onClose={() => setAddServerWizard(false)}
                    onJobStarted={(job) => {
                        setAddServerWizard(false)
                        setServerSetupJobs(prev => [...prev, job])
                        showToast(`Server setup started: ${job.serverIP}`, 'info')
                    }}
                    onServerAdded={() => {
                        setAddServerWizard(false)
                        fetchData(true)
                        showToast('Server added successfully', 'success')
                    }}
                    onError={(msg) => showToast(msg, 'error')}
                />
            )}

            {templateSelectModal && (
                <div className="modal-overlay" onClick={() => setTemplateSelectModal(null)}>
                    <div className="modal-content template-select-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Select Template</h3>
                            <button className="modal-close" onClick={() => setTemplateSelectModal(null)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="template-select-info">
                                Select a template to download and setup on <strong>{templateSelectModal.server.ip_address}</strong>
                            </p>
                            <div className="template-select-list">
                                {templateSelectModal.templates.map(template => (
                                    <div
                                        key={template.id}
                                        className="template-select-item"
                                        onClick={() => executeServerSetup(templateSelectModal.server, template)}
                                    >
                                        <div className="template-select-icon">
                                            <HardDrive size={24} />
                                        </div>
                                        <div className="template-select-details">
                                            <div className="template-select-name">
                                                {template.name}
                                                {template.vm_type === 'lxc' && <span className="status-badge running" style={{ fontSize: '10px', padding: '1px 5px', marginLeft: '6px' }}>LXC</span>}
                                            </div>
                                            <div className="template-select-meta">
                                                v{template.version} • {template.file_size_gb} GB • {(template.vm_type || 'qemu').toUpperCase()}
                                            </div>
                                        </div>
                                        <Download size={18} className="template-select-action" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

// Server Info Tab Component
function ServerInfoTab({ server, onClone, onBulkClone, onStartSetup, getResourceColor, getCapacityStatus, timeAgo, issues = [] }) {
    const ramPercent = server.ram_total_gb ? Math.round((server.ram_used_gb / server.ram_total_gb) * 100) : 0
    const diskPercent = server.disk_total_gb ? Math.round((server.disk_used_gb / server.disk_total_gb) * 100) : 0
    const capacity = getCapacityStatus(server.vm_count)

    return (
        <div className="server-info-tab">
            {/* Issues Alert */}
            {issues.length > 0 && (
                <div className="server-issues-card">
                    <div className="issues-header">
                        <AlertTriangle size={16} />
                        <span>{issues.length} Issue{issues.length > 1 ? 's' : ''} Detected</span>
                    </div>
                    <div className="issues-list">
                        {issues.map((issue, idx) => (
                            <div key={idx} className={`issue-item ${issue.type}`}>
                                <span className="issue-dot" />
                                <span>{issue.message}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Quick Actions */}
            <div className="info-actions">
                <button className="btn btn-primary" onClick={onClone}>
                    <Copy size={14} />
                    Clone VM
                </button>
                <button className="btn btn-secondary" onClick={onBulkClone}>
                    <Copy size={14} />
                    Bulk Clone
                </button>
                {server.setup_status !== 'downloading' && server.setup_status !== 'restoring' && (
                    <button className="btn btn-secondary" onClick={onStartSetup}>
                        <Download size={14} />
                        Setup QEMU Template
                    </button>
                )}
            </div>

            {/* Setup Progress */}
            {(server.setup_status === 'downloading' || server.setup_status === 'restoring') && (
                <div className="setup-progress-card">
                    <div className="setup-progress-header">
                        {server.setup_status === 'downloading' ? (
                            <Download size={16} className="downloading-icon" />
                        ) : (
                            <Settings size={16} className="spinning" />
                        )}
                        <span>{server.setup_status === 'downloading' ? 'Downloading Template' : 'Restoring VM'}</span>
                        <span className="setup-percent">{server.setup_progress || 0}%</span>
                    </div>
                    <div className="setup-progress-bar">
                        <div className="setup-progress-fill" style={{ width: `${server.setup_progress || 0}%` }} />
                    </div>
                    {server.setup_message && (
                        <div className="setup-message">{server.setup_message}</div>
                    )}
                </div>
            )}

            {server.setup_status === 'failed' && (
                <div className="setup-failed-card" onClick={onStartSetup}>
                    <AlertTriangle size={16} />
                    <span>Setup failed - click to retry</span>
                </div>
            )}

            {/* Stats Grid */}
            <div className="info-stats-grid">
                <div className="info-stat-card">
                    <div className="stat-header">
                        <HardDrive size={18} />
                        <span>Virtual Machines</span>
                    </div>
                    <div className="stat-value">{server.vm_running} / {server.vm_count}</div>
                    <div className="stat-label">running</div>
                    <span className={`capacity-badge ${capacity.status}`}>{capacity.label}</span>
                </div>

                <div className={`info-stat-card ${getResourceColor(server.cpu_usage || 0, 'cpu')}`}>
                    <div className="stat-header">
                        <Cpu size={18} />
                        <span>CPU Usage</span>
                    </div>
                    <div className="stat-value">{server.cpu_usage || 0}%</div>
                    <div className="resource-bar-mini">
                        <div className="bar-fill" style={{ width: `${server.cpu_usage || 0}%` }} />
                    </div>
                </div>

                <div className={`info-stat-card ${getResourceColor(ramPercent, 'ram')}`}>
                    <div className="stat-header">
                        <MemoryStick size={18} />
                        <span>Memory</span>
                    </div>
                    <div className="stat-value">{ramPercent}%</div>
                    <div className="stat-detail">
                        {server.ram_used_gb?.toFixed(1) || 0} / {server.ram_total_gb?.toFixed(0) || 0} GB
                    </div>
                    <div className="resource-bar-mini">
                        <div className="bar-fill" style={{ width: `${ramPercent}%` }} />
                    </div>
                </div>

                <div className={`info-stat-card ${getResourceColor(diskPercent, 'disk')}`}>
                    <div className="stat-header">
                        <Database size={18} />
                        <span>Storage</span>
                    </div>
                    <div className="stat-value">{diskPercent}%</div>
                    <div className="stat-detail">
                        {server.disk_used_gb?.toFixed(0) || 0} / {server.disk_total_gb?.toFixed(0) || 0} GB
                    </div>
                    <div className="resource-bar-mini">
                        <div className="bar-fill" style={{ width: `${diskPercent}%` }} />
                    </div>
                </div>
            </div>

            {/* Server Details */}
            <div className="info-details-card">
                <h3>Server Details</h3>
                <div className="details-grid">
                    <div className="detail-row">
                        <span className="detail-label">IP Address</span>
                        <span className="detail-value">{server.ip_address}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Name</span>
                        <span className="detail-value">{server.name || '-'}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Node</span>
                        <span className="detail-value">{server.node || 'pve'}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">QEMU Template</span>
                        <span className="detail-value">{server.template_vmid || '-'}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">LXC Template</span>
                        <span className="detail-value">{server.lxc_template_vmid || '-'}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Last Updated</span>
                        <span className="detail-value">{timeAgo(server.stats_updated_at)}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Setup Status</span>
                        <span className="detail-value">{server.setup_status || 'none'}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

// VMs Tab Component
function VMsTab({
    server, vms, allVMs, vmTab, setVmTab, searchQuery, setSearchQuery,
    dialerFilter, setDialerFilter, dialers, selectedVMs, toggleVMSelection,
    selectAllVMs, clearSelection, handleBulkVMAction, bulkActionLoading,
    vmActionLoading, handleVMAction, handleDeleteVM, setCloneModal, setBulkCloneModal,
    setConfigModal, servers, editingVmId, editingVmName, setEditingVmName,
    startInlineEdit, saveInlineEdit, handleInlineKeyDown, timeAgo
}) {
    return (
        <div className="vms-tab">
            {/* VM Toolbar */}
            <div className="vm-toolbar">
                <div className="vm-toolbar-left">
                    <div className="vm-tabs">
                        <button className={`tab ${vmTab === 'all' ? 'active' : ''}`} onClick={() => setVmTab('all')}>
                            All ({allVMs.length})
                        </button>
                        <button className={`tab ${vmTab === 'offline' ? 'active' : ''}`} onClick={() => setVmTab('offline')}>
                            Offline ({allVMs.filter(v => v.status !== 'running').length})
                        </button>
                        <button className={`tab ${vmTab === 'inactive' ? 'active' : ''}`} onClick={() => setVmTab('inactive')}>
                            Inactive 7d
                        </button>
                        <button className={`tab ${vmTab === 'unlinked' ? 'active' : ''}`} onClick={() => setVmTab('unlinked')}>
                            Unlinked ({allVMs.filter(v => !v.linked_sip).length})
                        </button>
                    </div>
                </div>
                <div className="vm-toolbar-right">
                    <div className="search-box">
                        <Search size={14} />
                        <input
                            type="text"
                            placeholder="Search VMs..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <SearchableDropdown
                        options={dialers}
                        value={dialerFilter}
                        onChange={setDialerFilter}
                        placeholder="Search dialers..."
                        allLabel="All Dialers"
                    />
                    <button className="btn btn-primary" onClick={() => setBulkCloneModal({ server })}>
                        <Copy size={14} />
                        Bulk Clone
                    </button>
                </div>
            </div>

            {/* Bulk Action Bar */}
            {selectedVMs.size > 0 && (
                <div className="bulk-action-bar">
                    <div className="bulk-left">
                        <span className="selection-count">{selectedVMs.size} selected</span>
                        <button className="btn-clear" onClick={clearSelection} disabled={bulkActionLoading}>
                            <X size={12} /> Clear
                        </button>
                    </div>
                    <div className="bulk-actions">
                        <button className="bulk-btn success" onClick={() => handleBulkVMAction('start')} disabled={bulkActionLoading} title="Start">
                            <Play size={14} />
                        </button>
                        <button className="bulk-btn warning" onClick={() => handleBulkVMAction('stop')} disabled={bulkActionLoading} title="Stop">
                            <Square size={14} />
                        </button>
                        <button className="bulk-btn" onClick={() => handleBulkVMAction('restart')} disabled={bulkActionLoading} title="Restart">
                            <RotateCcw size={14} />
                        </button>
                        <span className="bulk-divider"></span>
                        <button className="bulk-btn danger" onClick={() => handleBulkVMAction('delete')} disabled={bulkActionLoading} title="Delete">
                            <Trash2 size={14} />
                        </button>
                    </div>
                    {bulkActionLoading && (
                        <div className="bulk-loading">
                            <RefreshCw size={14} className="spin" />
                            <span>Processing...</span>
                        </div>
                    )}
                </div>
            )}

            {/* VM Table */}
            <div className="vm-table-container">
                <table className="vm-table">
                    <thead>
                        <tr>
                            <th className="checkbox-col">
                                <input
                                    type="checkbox"
                                    checked={selectedVMs.size > 0 && selectedVMs.size === vms.length}
                                    onChange={selectAllVMs}
                                    title="Select all"
                                />
                            </th>
                            <th>VMID</th>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>CPU%</th>
                            <th>RAM</th>
                            <th>Linked SIP</th>
                            <th>Dialer</th>
                            <th>Last Heartbeat</th>
                            <th className="actions-header">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vms.length === 0 ? (
                            <tr>
                                <td colSpan="11" className="empty-row">No VMs found</td>
                            </tr>
                        ) : (
                            vms.map(vm => (
                                <tr key={vm.vmid} className={selectedVMs.has(vm.vmid) ? 'selected' : ''}>
                                    <td className="checkbox-col">
                                        <input
                                            type="checkbox"
                                            checked={selectedVMs.has(vm.vmid)}
                                            onChange={() => toggleVMSelection(vm.vmid)}
                                        />
                                    </td>
                                    <td className="vmid">{vm.vmid}</td>
                                    <td className="vm-name-cell">
                                        {editingVmId === vm.vmid ? (
                                            <input
                                                type="text"
                                                className="inline-edit-input"
                                                value={editingVmName}
                                                onChange={(e) => setEditingVmName(e.target.value)}
                                                onKeyDown={(e) => handleInlineKeyDown(e, vm.vmid)}
                                                onBlur={() => saveInlineEdit(vm.vmid)}
                                                autoFocus
                                                disabled={vmActionLoading[vm.vmid] === 'rename'}
                                            />
                                        ) : (
                                            <span
                                                className="vm-name editable"
                                                onDoubleClick={() => startInlineEdit(vm)}
                                                title="Double-click to rename"
                                            >
                                                {vm.name}
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`status-badge ${vm.vm_type === 'lxc' ? 'running' : 'stopped'}`} style={{ fontSize: '10px', padding: '2px 6px' }}>
                                            {(vm.vm_type || 'qemu').toUpperCase()}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${vm.status}`}>
                                            {vm.status === 'running' ? <Wifi size={12} /> : <WifiOff size={12} />}
                                            {vm.status}
                                        </span>
                                    </td>
                                    <td className="usage-cell">
                                        {vm.status === 'running' ? (
                                            <div className="usage-bar-container" title={`CPU: ${vm.cpu_usage || 0}%`}>
                                                <div
                                                    className={`usage-bar ${vm.cpu_usage > 80 ? 'critical' : vm.cpu_usage > 50 ? 'warning' : 'normal'}`}
                                                    style={{ width: `${Math.min(100, vm.cpu_usage || 0)}%` }}
                                                />
                                                <span className="usage-text">{vm.cpu_usage || 0}%</span>
                                            </div>
                                        ) : <span className="usage-na">-</span>}
                                    </td>
                                    <td className="usage-cell">
                                        {vm.status === 'running' && vm.mem_used_gb && vm.memory_gb ? (
                                            <div className="usage-bar-container" title={`RAM: ${vm.mem_used_gb}/${vm.memory_gb} GB`}>
                                                <div
                                                    className={`usage-bar ${(vm.mem_used_gb / vm.memory_gb) > 0.9 ? 'critical' : (vm.mem_used_gb / vm.memory_gb) > 0.7 ? 'warning' : 'normal'}`}
                                                    style={{ width: `${Math.min(100, (vm.mem_used_gb / vm.memory_gb) * 100)}%` }}
                                                />
                                                <span className="usage-text">{vm.mem_used_gb}G</span>
                                            </div>
                                        ) : vm.memory_gb ? (
                                            <span className="usage-na">{vm.memory_gb}G</span>
                                        ) : <span className="usage-na">-</span>}
                                    </td>
                                    <td className="sip-cell">
                                        {vm.linked_sip || <span className="unlinked">Unlinked</span>}
                                    </td>
                                    <td>{vm.dialer_name || '-'}</td>
                                    <td className="time-cell">{vm.last_heartbeat ? timeAgo(vm.last_heartbeat) : '-'}</td>
                                    <td className="actions-cell">
                                        {vmActionLoading[vm.vmid] ? (
                                            <span className="action-loading">
                                                <RefreshCw size={14} className="spin" />
                                            </span>
                                        ) : vm.status === 'running' ? (
                                            <>
                                                <button className="action-btn" title="Stop" onClick={() => handleVMAction('stop', server.ip_address, vm.vmid, vm.name)}>
                                                    <Square size={14} />
                                                </button>
                                                <button className="action-btn" title="Restart" onClick={() => handleVMAction('restart', server.ip_address, vm.vmid, vm.name)}>
                                                    <RotateCcw size={14} />
                                                </button>
                                            </>
                                        ) : (
                                            <button className="action-btn success" title="Start" onClick={() => handleVMAction('start', server.ip_address, vm.vmid, vm.name)}>
                                                <Play size={14} />
                                            </button>
                                        )}
                                        <button className="action-btn" title="Configure" onClick={() => setConfigModal({ vm, serverIP: server.ip_address })} disabled={!!vmActionLoading[vm.vmid]}>
                                            <Settings size={14} />
                                        </button>
                                        <button className="action-btn" title="Clone from Template" onClick={() => setCloneModal({ server })} disabled={!!vmActionLoading[vm.vmid]}>
                                            <Copy size={14} />
                                        </button>
                                        <button className="action-btn danger" title="Delete" onClick={() => handleDeleteVM(server.ip_address, vm.vmid, vm.name)} disabled={!!vmActionLoading[vm.vmid]}>
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

// Clone VM Modal - Template only (no VM cloning allowed)
function CloneVMModal({ server, templates, onClose, onJobStarted, onError }) {
    const [vmType, setVmType] = useState(server?.lxc_template_vmid ? 'lxc' : 'qemu')
    const [templateId, setTemplateId] = useState('')
    const [autoActivate, setAutoActivate] = useState(true)
    const [startAfterClone, setStartAfterClone] = useState(true)
    const [loading, setLoading] = useState(false)

    // Source VMID is always the server's template - no VM cloning allowed
    const sourceVmid = vmType === 'lxc' ? server?.lxc_template_vmid : server?.template_vmid
    const hasTemplate = !!sourceVmid

    const selectedTemplate = templates.find(t => t.id === parseInt(templateId))

    const handleClone = async () => {
        if (!sourceVmid) {
            onError(`No ${vmType === 'lxc' ? 'LXC' : 'QEMU'} template configured for this server`)
            return
        }
        if (!templateId) {
            onError('Please select a bot template')
            return
        }

        setLoading(true)
        try {
            const result = await monitoringApi.cloneVMAsync(
                server.ip_address,
                parseInt(sourceVmid),
                selectedTemplate.name,
                { startAfterClone, configureDhcp: true, templateId: parseInt(templateId), autoActivate, vm_type: vmType }
            )

            if (result.data.success && result.data.jobId) {
                onJobStarted({
                    jobId: result.data.jobId,
                    vmName: result.data.vmName,
                    status: 'starting',
                    progress: 0,
                    message: 'Initializing clone...'
                })
            } else {
                onError(result.data.error || 'Failed to start clone job')
            }
        } catch (err) {
            onError(err.response?.data?.error || err.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal clone-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Clone VM</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    <div className="form-group">
                        <label>VM Type</label>
                        <select value={vmType} onChange={e => setVmType(e.target.value)}>
                            {server?.lxc_template_vmid && <option value="lxc">LXC Container (Recommended)</option>}
                            <option value="qemu">QEMU VM</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Source {vmType === 'lxc' ? 'LXC' : 'VM'} Template</label>
                        {hasTemplate ? (
                            <div className="static-value template-source">
                                {vmType === 'lxc' ? 'LXC' : 'QEMU'} Template (VMID: {sourceVmid})
                            </div>
                        ) : (
                            <div className="static-value template-missing">
                                No {vmType === 'lxc' ? 'LXC' : 'QEMU'} template configured for this server.
                                Please set up the server first.
                            </div>
                        )}
                    </div>
                    <div className="form-group">
                        <label>Target Server</label>
                        <div className="static-value">{server.name || server.ip_address}</div>
                    </div>
                    <div className="form-group">
                        <label>Template</label>
                        <select value={templateId} onChange={e => setTemplateId(e.target.value)}>
                            <option value="">Select a template...</option>
                            {templates.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group checkbox-group">
                        <label>
                            <input type="checkbox" checked={autoActivate} onChange={e => setAutoActivate(e.target.checked)} />
                            Pre-Approved (auto-assign license and rename)
                        </label>
                    </div>
                    <div className="form-group checkbox-group">
                        <label>
                            <input type="checkbox" checked={startAfterClone} onChange={e => setStartAfterClone(e.target.checked)} />
                            Start {vmType === 'lxc' ? 'LXC' : 'VM'} after clone
                        </label>
                    </div>
                    {autoActivate && (
                        <div className="info-banner">
                            <CheckCircle size={14} />
                            <span>Clone will be auto-approved, assigned a license with next available SIP, and renamed to match.</span>
                        </div>
                    )}
                </div>
                <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleClone} disabled={loading || !sourceVmid || !templateId}>
                        {loading ? <RefreshCw size={14} className="spin" /> : <Copy size={14} />}
                        Clone {vmType === 'lxc' ? 'LXC' : 'VM'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// Config VM Modal
function ConfigVMModal({ vm, serverIP, onClose, onSuccess, onError }) {
    const [vmName, setVmName] = useState(vm.name || '')
    const [cores, setCores] = useState(vm.cores || 2)
    const [memoryMb, setMemoryMb] = useState(vm.memory_mb || 4096)
    const [loading, setLoading] = useState(false)
    const [renameLoading, setRenameLoading] = useState(false)

    const vmIsRunning = vm.status === 'running'
    const nameChanged = vmName !== vm.name
    const configChanged = cores !== vm.cores || memoryMb !== vm.memory_mb

    const handleRename = async () => {
        if (!nameChanged || !vmName.trim()) return
        setRenameLoading(true)
        try {
            const result = await monitoringApi.renameVM(serverIP, vm.vmid, vmName.trim())
            if (result.data.success) {
                onSuccess('VM renamed successfully')
            } else {
                onError(result.data.error || 'Rename failed')
            }
        } catch (err) {
            onError(err.response?.data?.error || err.message)
        } finally {
            setRenameLoading(false)
        }
    }

    const handleSaveConfig = async () => {
        setLoading(true)
        try {
            const result = await monitoringApi.updateVMConfig(serverIP, vm.vmid, cores, memoryMb)
            if (result.data.requires_stop) {
                onError('VM must be stopped to change configuration')
                return
            }
            if (result.data.success) {
                onSuccess('VM configuration updated')
            } else {
                onError(result.data.error || 'Update failed')
            }
        } catch (err) {
            onError(err.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal config-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Configure VM: {vm.name}</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    <div className="form-section">
                        <h4>VM Name</h4>
                        <div className="form-group inline-action">
                            <input type="text" value={vmName} onChange={e => setVmName(e.target.value)} placeholder="Enter VM name" disabled={renameLoading} />
                            <button className="btn btn-sm btn-primary" onClick={handleRename} disabled={renameLoading || !nameChanged || !vmName.trim()}>
                                {renameLoading ? <RefreshCw size={12} className="spin" /> : null}
                                Rename
                            </button>
                        </div>
                        <span className="helper-text">VMID: {vm.vmid}</span>
                    </div>
                    <div className="form-section">
                        <h4>Hardware Configuration</h4>
                        {vmIsRunning && (
                            <div className="warning-banner">
                                <AlertTriangle size={16} />
                                VM must be stopped to change CPU/RAM
                            </div>
                        )}
                        <div className="form-group">
                            <label>CPU Cores</label>
                            <select value={cores} onChange={e => setCores(parseInt(e.target.value))} disabled={vmIsRunning}>
                                {[1, 2, 3, 4, 6, 8].map(n => (
                                    <option key={n} value={n}>{n} cores</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>RAM (MB)</label>
                            <select value={memoryMb} onChange={e => setMemoryMb(parseInt(e.target.value))} disabled={vmIsRunning}>
                                {[2048, 4096, 6144, 8192, 12288, 16384].map(n => (
                                    <option key={n} value={n}>{(n / 1024).toFixed(0)} GB ({n} MB)</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                    <button className="btn btn-primary" onClick={handleSaveConfig} disabled={loading || vmIsRunning || !configChanged}>
                        {loading ? <RefreshCw size={14} className="spin" /> : <Settings size={14} />}
                        Apply Hardware Changes
                    </button>
                </div>
            </div>
        </div>
    )
}

// Bulk Clone Modal
function BulkCloneModal({ server, templates, onClose, onQueueStarted, onError }) {
    const [vmType, setVmType] = useState(server?.lxc_template_vmid ? 'lxc' : 'qemu')
    const [templateId, setTemplateId] = useState('')
    const [cloneCount, setCloneCount] = useState(1)
    const [autoActivate, setAutoActivate] = useState(true)
    const [startAfterClone, setStartAfterClone] = useState(true)
    const [loading, setLoading] = useState(false)

    // Source VMID is always the server's template - no manual selection allowed
    const sourceVmid = vmType === 'lxc' ? server?.lxc_template_vmid : server?.template_vmid
    const hasTemplate = !!sourceVmid

    const handleBulkClone = async () => {
        if (!sourceVmid || !templateId || cloneCount < 1) {
            onError('Please select source VM, template, and enter clone count')
            return
        }

        setLoading(true)
        try {
            const result = await monitoringApi.bulkClone(
                server.ip_address,
                parseInt(sourceVmid),
                parseInt(templateId),
                cloneCount,
                { autoActivate, startAfterClone, configureDhcp: true, vm_type: vmType }
            )

            if (result.data.success && result.data.queueId) {
                onQueueStarted({
                    queueId: result.data.queueId,
                    total: cloneCount,
                    completed: 0,
                    failed: 0,
                    status: 'running',
                    jobs: result.data.jobs || []
                })
            } else {
                onError(result.data.error || 'Failed to start bulk clone')
            }
        } catch (err) {
            onError(err.response?.data?.error || err.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal bulk-clone-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Bulk Clone VMs</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    <div className="form-group">
                        <label>VM Type</label>
                        <select value={vmType} onChange={e => setVmType(e.target.value)}>
                            {server?.lxc_template_vmid && <option value="lxc">LXC Container (Recommended)</option>}
                            <option value="qemu">QEMU VM</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Source {vmType === 'lxc' ? 'LXC' : 'VM'} Template</label>
                        {hasTemplate ? (
                            <div className="static-value template-source">
                                {vmType === 'lxc' ? 'LXC' : 'QEMU'} Template (VMID: {sourceVmid})
                            </div>
                        ) : (
                            <div className="static-value template-missing">
                                No {vmType === 'lxc' ? 'LXC' : 'QEMU'} template configured for this server.
                                Please set up the server first.
                            </div>
                        )}
                    </div>
                    <div className="form-group">
                        <label>Target Server</label>
                        <div className="static-value">{server.name || server.ip_address}</div>
                    </div>
                    <div className="form-group">
                        <label>Template (for license assignment)</label>
                        <SearchableDropdown
                            options={templates}
                            value={templateId}
                            onChange={setTemplateId}
                            placeholder="Select a template..."
                            showAll={false}
                            displayKey="name"
                            valueKey="id"
                        />
                    </div>
                    <div className="form-group">
                        <label>Number of Clones</label>
                        <input
                            type="number"
                            min="1"
                            max="20"
                            value={cloneCount}
                            onChange={e => setCloneCount(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
                        />
                        <span className="helper-text">Max 20 clones per batch</span>
                    </div>
                    <div className="form-group checkbox-group">
                        <label>
                            <input type="checkbox" checked={autoActivate} onChange={e => setAutoActivate(e.target.checked)} />
                            Pre-Approved (auto-assign license and rename)
                        </label>
                    </div>
                    <div className="form-group checkbox-group">
                        <label>
                            <input type="checkbox" checked={startAfterClone} onChange={e => setStartAfterClone(e.target.checked)} />
                            Start {vmType === 'lxc' ? 'LXC' : 'VM'} after clone
                        </label>
                    </div>
                    {autoActivate && (
                        <div className="info-banner">
                            <CheckCircle size={14} />
                            <span>Each clone will be auto-approved, assigned a license with next available SIP.</span>
                        </div>
                    )}
                </div>
                <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleBulkClone} disabled={loading || !hasTemplate || !templateId || cloneCount < 1}>
                        {loading ? <RefreshCw size={14} className="spin" /> : <Copy size={14} />}
                        Clone {cloneCount} {vmType === 'lxc' ? 'LXC' : 'VM'}{cloneCount > 1 ? 's' : ''}
                    </button>
                </div>
            </div>
        </div>
    )
}

// Delete VM Dialog
function DeleteVMDialog({ dialog, onClose, onConfirmSingle, onConfirmBulk }) {
    const [password, setPassword] = useState('')
    const [deleteLicense, setDeleteLicense] = useState(false)
    const [error, setError] = useState('')

    const DELETE_PASSWORD = '474747'
    const isSingle = dialog.type === 'single'
    const vmCount = isSingle ? 1 : dialog.vmids?.length || 0

    const handleConfirm = () => {
        if (password !== DELETE_PASSWORD) {
            setError('Incorrect password')
            return
        }
        if (isSingle) {
            onConfirmSingle(dialog.serverIP, dialog.vmid, dialog.vmName, deleteLicense)
        } else {
            onConfirmBulk(dialog.vmids, deleteLicense)
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && password === DELETE_PASSWORD) {
            handleConfirm()
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal delete-vm-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Delete {isSingle ? 'VM' : `${vmCount} VMs`}?</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    {isSingle ? (
                        <p className="delete-warning">
                            You are about to delete <strong>{dialog.vmName}</strong> (VMID: {dialog.vmid}).
                        </p>
                    ) : (
                        <p className="delete-warning">
                            You are about to delete <strong>{vmCount} VMs</strong>.
                        </p>
                    )}
                    <div className="form-group checkbox-group">
                        <label>
                            <input type="checkbox" checked={deleteLicense} onChange={e => setDeleteLicense(e.target.checked)} />
                            Also delete associated License, Bot Status, and Machine entry
                        </label>
                    </div>
                    <div className="form-group">
                        <label>Enter password to confirm</label>
                        <input
                            type="password"
                            value={password}
                            onChange={e => { setPassword(e.target.value); setError(''); }}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter deletion password..."
                            autoFocus
                        />
                        {error && <span className="error-text">{error}</span>}
                    </div>
                </div>
                <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn-danger" onClick={handleConfirm} disabled={password !== DELETE_PASSWORD}>
                        Delete {isSingle ? 'VM' : `${vmCount} VMs`}
                    </button>
                </div>
            </div>
        </div>
    )
}

// Add Server Wizard (simplified for now - will use the same from original)
function AddServerWizard({ onClose, onJobStarted, onServerAdded, onError }) {
    const [mode, setMode] = useState('single') // 'single' or 'bulk'
    const [step, setStep] = useState(1) // 1: Server Info, 2: Template Selection

    // Single mode state
    const [ipAddress, setIpAddress] = useState('')
    const [serverName, setServerName] = useState('')
    const [username, setUsername] = useState('root@pam')
    const [password, setPassword] = useState('')
    const [node, setNode] = useState('pve')
    const [port, setPort] = useState('')
    const [testLoading, setTestLoading] = useState(false)
    const [testResult, setTestResult] = useState(null)
    const [connectionVerified, setConnectionVerified] = useState(false)

    // Bulk mode state - list of servers to add
    const [bulkServers, setBulkServers] = useState([])
    const [bulkIpInput, setBulkIpInput] = useState('')

    // Template state
    const [templates, setTemplates] = useState([])
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [loadingTemplates, setLoadingTemplates] = useState(false)
    const [skipTemplate, setSkipTemplate] = useState(false)

    // Adding state
    const [addLoading, setAddLoading] = useState(false)
    const [addedServers, setAddedServers] = useState([])

    // Test connection (single mode)
    const handleTestConnection = async () => {
        if (!ipAddress) {
            onError('Please enter server IP address')
            return
        }

        setTestLoading(true)
        setTestResult(null)
        setConnectionVerified(false)

        try {
            const result = await monitoringApi.testServerConnection({
                ip_address: ipAddress,
                username,
                password,
                node,
                port: port ? parseInt(port) : undefined
            })

            if (result.data.success) {
                setTestResult({
                    success: true,
                    message: 'Connection successful!',
                    info: result.data.server_info
                })
                setConnectionVerified(true)
            } else {
                setTestResult({
                    success: false,
                    message: result.data.error || 'Connection failed'
                })
            }
        } catch (err) {
            setTestResult({
                success: false,
                message: err.response?.data?.error || err.message
            })
        } finally {
            setTestLoading(false)
        }
    }

    // Parse bulk IP input
    const parseBulkIps = () => {
        const ips = bulkIpInput
            .split(/[\n,;]+/)
            .map(ip => ip.trim())
            .filter(ip => ip && /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ip))

        const uniqueIps = [...new Set(ips)]
        const newServers = uniqueIps.map(ip => ({
            ip,
            status: 'pending',
            error: null
        }))

        setBulkServers(newServers)
    }

    // Test all bulk servers
    const handleTestBulkConnections = async () => {
        for (let i = 0; i < bulkServers.length; i++) {
            const server = bulkServers[i]
            if (server.status === 'verified' || server.status === 'added') continue

            setBulkServers(prev => prev.map((s, idx) =>
                idx === i ? { ...s, status: 'testing', error: null } : s
            ))

            try {
                const result = await monitoringApi.testServerConnection({
                    ip_address: server.ip,
                    username,
                    password,
                    node
                })

                setBulkServers(prev => prev.map((s, idx) =>
                    idx === i ? {
                        ...s,
                        status: result.data.success ? 'verified' : 'failed',
                        error: result.data.success ? null : (result.data.error || 'Connection failed'),
                        info: result.data.server_info
                    } : s
                ))
            } catch (err) {
                setBulkServers(prev => prev.map((s, idx) =>
                    idx === i ? {
                        ...s,
                        status: 'failed',
                        error: err.response?.data?.error || err.message
                    } : s
                ))
            }
        }
    }

    // Remove server from bulk list
    const removeBulkServer = (index) => {
        setBulkServers(prev => prev.filter((_, i) => i !== index))
    }

    // Load templates when moving to step 2
    useEffect(() => {
        if (step === 2 && templates.length === 0) {
            setLoadingTemplates(true)
            monitoringApi.getVMTemplates()
                .then(res => {
                    if (res.data.success && res.data.templates) {
                        setTemplates(res.data.templates)
                        if (res.data.templates.length > 0) {
                            setSelectedTemplate(res.data.templates[0])
                        }
                    }
                })
                .catch(err => {
                    console.error('Error loading templates:', err)
                })
                .finally(() => setLoadingTemplates(false))
        }
    }, [step])

    // Add single server
    const handleAddSingleServer = async (withTemplate = true) => {
        setAddLoading(true)

        try {
            const addResult = await monitoringApi.addServer({
                ip_address: ipAddress,
                name: serverName || ipAddress,
                username,
                password,
                node,
                port: port ? parseInt(port) : undefined
            })

            if (!addResult.data.success) {
                onError(addResult.data.error || 'Failed to add server')
                setAddLoading(false)
                return
            }

            const newServerId = addResult.data.server_id

            if (!withTemplate || skipTemplate) {
                setAddedServers(prev => [...prev, { ip: ipAddress, serverId: newServerId }])
                onServerAdded()
                return
            }

            // Start template setup - this runs in background
            const setupResult = await monitoringApi.startServerSetup(newServerId, selectedTemplate?.id)

            if (setupResult.data.success) {
                setAddedServers(prev => [...prev, { ip: ipAddress, serverId: newServerId, jobId: setupResult.data.jobId }])
                onServerAdded()
            } else {
                onError(`Server added but setup failed: ${setupResult.data.error}`)
                onServerAdded()
            }
        } catch (err) {
            onError(err.response?.data?.error || err.message)
        } finally {
            setAddLoading(false)
        }
    }

    // Add all verified bulk servers
    const handleAddBulkServers = async (withTemplate = true) => {
        setAddLoading(true)
        const verifiedServers = bulkServers.filter(s => s.status === 'verified')

        for (let i = 0; i < verifiedServers.length; i++) {
            const server = verifiedServers[i]
            const serverIndex = bulkServers.findIndex(s => s.ip === server.ip)

            setBulkServers(prev => prev.map((s, idx) =>
                idx === serverIndex ? { ...s, status: 'adding' } : s
            ))

            try {
                const addResult = await monitoringApi.addServer({
                    ip_address: server.ip,
                    name: server.ip,
                    username,
                    password,
                    node,
                    port: port ? parseInt(port) : undefined
                })

                if (addResult.data.success) {
                    const newServerId = addResult.data.server_id

                    if (withTemplate && !skipTemplate && selectedTemplate) {
                        await monitoringApi.startServerSetup(newServerId, selectedTemplate.id)
                    }

                    setBulkServers(prev => prev.map((s, idx) =>
                        idx === serverIndex ? { ...s, status: 'added', serverId: newServerId } : s
                    ))
                } else {
                    setBulkServers(prev => prev.map((s, idx) =>
                        idx === serverIndex ? { ...s, status: 'failed', error: addResult.data.error } : s
                    ))
                }
            } catch (err) {
                setBulkServers(prev => prev.map((s, idx) =>
                    idx === serverIndex ? { ...s, status: 'failed', error: err.message } : s
                ))
            }
        }

        setAddLoading(false)
        onServerAdded()
    }

    const verifiedCount = bulkServers.filter(s => s.status === 'verified').length
    const canProceedBulk = verifiedCount > 0

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal add-server-modal wizard-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>
                        <Plus size={18} style={{ marginRight: 8 }} />
                        Add {mode === 'bulk' ? 'Multiple Servers' : 'New Server'}
                    </h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                {/* Mode Toggle */}
                <div className="wizard-mode-toggle">
                    <button
                        className={`mode-btn ${mode === 'single' ? 'active' : ''}`}
                        onClick={() => { setMode('single'); setStep(1); }}
                    >
                        <Server size={14} /> Single Server
                    </button>
                    <button
                        className={`mode-btn ${mode === 'bulk' ? 'active' : ''}`}
                        onClick={() => { setMode('bulk'); setStep(1); }}
                    >
                        <Copy size={14} /> Bulk Add
                    </button>
                </div>

                {/* Step Indicators */}
                <div className="wizard-steps">
                    <div className={`wizard-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                        <div className="step-number">{step > 1 ? <CheckCircle size={14} /> : '1'}</div>
                        <span>Connection</span>
                    </div>
                    <div className="step-connector" />
                    <div className={`wizard-step ${step >= 2 ? 'active' : ''}`}>
                        <div className="step-number">2</div>
                        <span>Template</span>
                    </div>
                </div>

                <div className="modal-body">
                    {/* Step 1: Single Server Connection */}
                    {step === 1 && mode === 'single' && (
                        <div className="wizard-step-content">
                            <div className="form-group">
                                <label>Server IP Address *</label>
                                <input
                                    type="text"
                                    value={ipAddress}
                                    onChange={e => { setIpAddress(e.target.value); setConnectionVerified(false); }}
                                    placeholder="e.g., 10.10.10.20"
                                    autoFocus
                                />
                            </div>

                            <div className="form-group">
                                <label>Server Name (optional)</label>
                                <input
                                    type="text"
                                    value={serverName}
                                    onChange={e => setServerName(e.target.value)}
                                    placeholder="e.g., Proxmox Server 2"
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Username</label>
                                    <input
                                        type="text"
                                        value={username}
                                        onChange={e => { setUsername(e.target.value); setConnectionVerified(false); }}
                                        placeholder="root@pam"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Password</label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={e => { setPassword(e.target.value); setConnectionVerified(false); }}
                                        placeholder="Enter password"
                                    />
                                </div>
                            </div>

                            <div className="form-row" style={{ display: 'flex', gap: '12px' }}>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>Node Name</label>
                                    <input
                                        type="text"
                                        value={node}
                                        onChange={e => { setNode(e.target.value); setConnectionVerified(false); }}
                                        placeholder="pve"
                                    />
                                    <span className="helper-text">Usually "pve"</span>
                                </div>
                                <div className="form-group" style={{ flex: 0, minWidth: '100px' }}>
                                    <label>API Port</label>
                                    <input
                                        type="number"
                                        value={port}
                                        onChange={e => { setPort(e.target.value); setConnectionVerified(false); }}
                                        placeholder="8006"
                                    />
                                    <span className="helper-text">Default: 8006</span>
                                </div>
                            </div>

                            <button
                                className={`btn test-connection-btn ${connectionVerified ? 'btn-success' : 'btn-secondary'}`}
                                onClick={handleTestConnection}
                                disabled={testLoading || !ipAddress}
                            >
                                {testLoading ? (
                                    <><RefreshCw size={14} className="spin" /> Testing...</>
                                ) : connectionVerified ? (
                                    <><CheckCircle size={14} /> Connected</>
                                ) : (
                                    <><Wifi size={14} /> Test Connection</>
                                )}
                            </button>

                            {testResult && (
                                <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                                    {testResult.success ? <CheckCircle size={14} /> : <XCircle size={14} />}
                                    <span>{testResult.message}</span>
                                    {testResult.info && (
                                        <div className="server-info">
                                            <span>CPU: {testResult.info.cpu_cores} cores</span>
                                            <span>RAM: {testResult.info.ram_total_gb} GB</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 1: Bulk Server Input */}
                    {step === 1 && mode === 'bulk' && (
                        <div className="wizard-step-content">
                            <div className="form-group">
                                <label>Enter IP Addresses (one per line or comma-separated)</label>
                                <textarea
                                    value={bulkIpInput}
                                    onChange={e => setBulkIpInput(e.target.value)}
                                    placeholder="65.109.109.95&#10;65.109.109.96&#10;65.109.109.97"
                                    rows={4}
                                    style={{ fontFamily: 'var(--font-mono)' }}
                                />
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={parseBulkIps}
                                    style={{ marginTop: 8 }}
                                >
                                    Parse IPs
                                </button>
                            </div>

                            {bulkServers.length > 0 && (
                                <>
                                    <div className="form-row" style={{ marginBottom: 12 }}>
                                        <div className="form-group" style={{ flex: 1 }}>
                                            <label>Username (same for all)</label>
                                            <input
                                                type="text"
                                                value={username}
                                                onChange={e => setUsername(e.target.value)}
                                                placeholder="root@pam"
                                            />
                                        </div>
                                        <div className="form-group" style={{ flex: 1 }}>
                                            <label>Password (same for all)</label>
                                            <input
                                                type="password"
                                                value={password}
                                                onChange={e => setPassword(e.target.value)}
                                                placeholder="Enter password"
                                            />
                                        </div>
                                    </div>

                                    <div className="bulk-server-list">
                                        <div className="bulk-header">
                                            <span>{bulkServers.length} servers</span>
                                            <button
                                                className="btn btn-secondary btn-sm"
                                                onClick={handleTestBulkConnections}
                                                disabled={testLoading}
                                            >
                                                {testLoading ? <RefreshCw size={12} className="spin" /> : <Wifi size={12} />}
                                                Test All
                                            </button>
                                        </div>
                                        {bulkServers.map((server, idx) => (
                                            <div key={server.ip} className={`bulk-server-item ${server.status}`}>
                                                <span className="bulk-ip">{server.ip}</span>
                                                <span className={`bulk-status ${server.status}`}>
                                                    {server.status === 'pending' && 'Pending'}
                                                    {server.status === 'testing' && <><RefreshCw size={12} className="spin" /> Testing</>}
                                                    {server.status === 'verified' && <><CheckCircle size={12} /> Verified</>}
                                                    {server.status === 'failed' && <><XCircle size={12} /> Failed</>}
                                                    {server.status === 'adding' && <><RefreshCw size={12} className="spin" /> Adding</>}
                                                    {server.status === 'added' && <><CheckCircle size={12} /> Added</>}
                                                </span>
                                                {server.error && <span className="bulk-error">{server.error}</span>}
                                                <button
                                                    className="bulk-remove"
                                                    onClick={() => removeBulkServer(idx)}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Step 2: Template Selection */}
                    {step === 2 && (
                        <div className="wizard-step-content">
                            <p className="step-description">
                                Download and restore a Redstone VM template to {mode === 'bulk' ? 'each server' : 'this server'}.
                                This creates a ready-to-clone VM with all bot software pre-installed.
                            </p>

                            {loadingTemplates ? (
                                <div className="loading-templates">
                                    <RefreshCw size={20} className="spin" />
                                    <span>Loading templates...</span>
                                </div>
                            ) : (
                                <div className="template-cards">
                                    {templates.map(template => (
                                        <div
                                            key={template.id}
                                            className={`template-card ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedTemplate(template)}
                                        >
                                            <div className="template-icon">
                                                <Download size={24} />
                                            </div>
                                            <div className="template-info">
                                                <h4>{template.name}</h4>
                                                <span className="template-version">v{template.version}</span>
                                                <span className="template-size">{template.file_size_gb} GB</span>
                                            </div>
                                            {selectedTemplate?.id === template.id && (
                                                <CheckCircle className="template-check" size={20} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="form-group checkbox-group" style={{ marginTop: 16 }}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={skipTemplate}
                                        onChange={e => setSkipTemplate(e.target.checked)}
                                    />
                                    Skip template setup (add server{mode === 'bulk' ? 's' : ''} only)
                                </label>
                                <span className="helper-text">
                                    You can set up the template later from the server settings
                                </span>
                            </div>

                            {!skipTemplate && selectedTemplate && (
                                <div className="info-banner">
                                    <Download size={14} />
                                    <span>
                                        {mode === 'bulk' ? (
                                            `Template (${selectedTemplate.file_size_gb} GB) will download to all ${verifiedCount} servers simultaneously using azcopy.`
                                        ) : (
                                            `Template (${selectedTemplate.file_size_gb} GB) will be downloaded using fast azcopy transfer.`
                                        )}
                                    </span>
                                </div>
                            )}

                            {!skipTemplate && (
                                <div className="info-banner info-banner-secondary" style={{ marginTop: 8 }}>
                                    <RefreshCw size={14} />
                                    <span>
                                        Downloads run in background. You can close this dialog and monitor progress on server cards.
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="modal-actions">
                    {step === 1 && mode === 'single' && (
                        <>
                            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                            <button
                                className="btn btn-primary"
                                onClick={() => setStep(2)}
                                disabled={!connectionVerified}
                            >
                                Next
                                <ChevronRight size={14} style={{ marginLeft: 4 }} />
                            </button>
                        </>
                    )}

                    {step === 1 && mode === 'bulk' && (
                        <>
                            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                            <button
                                className="btn btn-primary"
                                onClick={() => setStep(2)}
                                disabled={!canProceedBulk}
                            >
                                Next ({verifiedCount} servers)
                                <ChevronRight size={14} style={{ marginLeft: 4 }} />
                            </button>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <button className="btn btn-secondary" onClick={() => setStep(1)}>
                                Back
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => mode === 'bulk' ? handleAddBulkServers(!skipTemplate) : handleAddSingleServer(!skipTemplate)}
                                disabled={addLoading || (!skipTemplate && !selectedTemplate)}
                            >
                                {addLoading ? (
                                    <><RefreshCw size={14} className="spin" /> Adding...</>
                                ) : skipTemplate ? (
                                    <><Plus size={14} /> Add Server{mode === 'bulk' ? 's' : ''}</>
                                ) : (
                                    <><Upload size={14} /> Add & Setup {mode === 'bulk' ? `${verifiedCount} Servers` : 'Server'}</>
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
